import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface SettingsMenuProps {
  showDebugOption?: boolean;
}

const SettingsMenu: React.FC<SettingsMenuProps> = ({
  showDebugOption = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [debugEnabled, setDebugEnabled] = useState(
    localStorage.getItem("debugGameEnabled") === "true"
  );
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDebugMode = () => {
    if (debugEnabled) {
      localStorage.removeItem("debugGameEnabled");
      setDebugEnabled(false);
    } else {
      localStorage.setItem("debugGameEnabled", "true");
      setDebugEnabled(true);
    }
  };

  const openDebugPanel = () => {
    navigate("/debug");
    setIsOpen(false);
  };

  const visitDeveloperSite = () => {
    window.open("https://ankitchandel.in", "_blank");
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-full hover:bg-slate-700/50 transition-colors"
        aria-label="Settings"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-6 h-6 text-slate-300"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-slate-800 border border-slate-700 z-50 animate-fadeIn">
          <div className="py-1">
            {showDebugOption && (
              <>
                <div className="px-4 py-2 flex items-center justify-between">
                  <span className="text-sm text-slate-300">Debug Mode</span>
                  <button
                    onClick={toggleDebugMode}
                    className={`relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                      debugEnabled ? "bg-indigo-600" : "bg-slate-600"
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ease-in-out ${
                        debugEnabled ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
                <button
                  onClick={openDebugPanel}
                  className="block w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700"
                >
                  Open Debug Panel
                </button>
                <div className="border-t border-slate-700 my-1"></div>
              </>
            )}
            <button
              onClick={visitDeveloperSite}
              className="block w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700"
            >
              <span className="flex items-center">
                <span>Developer Info</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 ml-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsMenu;
